<template>
    <div v-if="note" class="note-view" >
        <portal to="navbar-actions">
            <mode-button class="mode-switcher" :options="modes" :values="note_view_modes" @select="setViewMode"/>

        </portal>
        <div id="header">
            <div class="header-row title-row">
                <!-- <i class="icon-button action clickable fas  fa-cogs "       id="options" @click="setModalOpen"/> -->
                <span v-if="!editing" class="note-title" ref="header_text">
                    {{note.title}}
                </span>
            
                <input v-else ref="hidden_input"  
                    type="text"
                    class="note-title-input"
                    v-model="note_title"
                    placeholder="Name your note:"
                />
            </div>

            <div v-if="pages != null" class="header-row toolbar">
                <portal-target v-if="editing" class="edit-toolbar" name="note-header"/>
                <div  class="markdown-header" :class="{'half': editing}">
                    <div class="header-button clickable" @click="prevPage">
                        prev
                    </div>

                    <div class="header-button">
                        {{ current_page+1 }} / {{ pages }}
                    </div>

                    <div class="header-button clickable" @click="nextPage">
                        next
                    </div>
                </div>

            </div>
        </div>
       
        <!-- Note editor and viewer -->
        <div class="content">
            <div id="editor-wrapper" class="content-segment" v-if="editing">
                <note-editor  id="note-editor"
                    :note="note"
                    :markdown='note.text' 
                    @focus="isMobile ? (editor_focused=true): (editor_focused=false)"
                    @blur="isMobile ? (editor_focused=false): (editor_focused=false)"
                />
            </div>

            <div id="markdown-wrapper" class="content-segment" :class="editor_focused ? 'editor-focused' : ''">
                <markdown driver="markdown-it" @Ready="setPages" :current_page="current_page" ref="markdown" :source="note_text_with_links"  id="markdown-viewer" highlight html/>
            </div>
        </div>

        <note-options/>
    </div>
</template>

<script>
import ModeButton from '../components/ModeButton.vue'
import NoteOptions from '../components/Modals/NoteOptions.vue'
import Markdown from '../components/Markdown.vue'
import NoteEditor from '../components/NoteEditor.vue'

import _ from 'lodash';


function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

export default {
    components: { NoteEditor, Markdown, NoteOptions, ModeButton },
    props: [],
    data(){
        return{
            new_alias: '',
            note_title_input: this.note ? this.note.title : '',
            // options_modal_open: false,
            note_alias_error: null,
            editor_focused: null,
            current_page: 0,
            pages: null,
            show_header: false,
            modes: [
                {
                    icon_class: "fa-eye",
                    value: "view"
                },
                {
                    icon_class: "fa-pencil-alt",
                    value: "edit"
                },
                {
                    icon_class: "fa-cog",
                    value: "options",
                    toggle: true,
                    mutation: 'setNoteOptionsVisible',
                    getter: 'note_options_visible'
                },
            ],
        }
    },

    mounted(){
       
    },

    methods:{
        toggleMode(){
            //get the new route path
            let path = `/${this.note.workspace}/notes/${this.note.slug}`
            
            //If edit mode, add edit to the end
            if (!this.editing) path += "/edit";

            this.$router.push(path).catch(error => console.debug(error));
        },

        setViewMode(mode) {
            // This mess should be made part of the mode switch component; 
            //  each option should have a 'toggle' boolean with a mutation name to call with
            if (mode.toggle) {
                this.$store.commit(mode.mutation, !this.$store.getters[mode.getter]);
            } else {
                this.$store.commit("setViewMode", mode.value);
            }
        },

        createNote(){
            if(this.note_title_input.length) {
                this.$store.dispatch('addNoteToWorkspace', {
                    title: this.note_title_input,
                    parent_slug: this.$route.query.parent_slug
                }).then( note =>{
                    this.note_title_input = '';
                    this.$router.push({
                        path:`/${note.workspace}/notes/${note.slug}/edit`
                    });
                });   
            }
            
            else {
                this.$store.dispatch("toggleShowError", true);
                setTimeout( () => {
                    this.$store.dispatch("toggleShowError", false);
                }, 3000);
            }
        },


        
        deleteNote() {
            this.$store.dispatch('deleteNoteFromWorkspace', this.note).then(() => {
                this.$router.replace('/')
            });
        },

        setModalOpen(value = null, event) {
            this.$store.commit('setNoteOptionsVisible', value);
        },
        
        addAlias() {
            this.note_aliases= [...(this.note_aliases||[]), this.new_alias];
        },

        deleteAlias(index) {
            this.note_aliases.splice(index, 1);
        },

        nextPage() {
            if (this.current_page < this.pages-1) this.current_page++;
        },

        prevPage() {
            if (this.current_page > 0) this.current_page--;
        },

        setPages(pages) {
            this.pages = pages;
        }
    },

    watch: {
        new_alias() {
            this.note_alias_error = null;
        },

        note() {
            this.current_page = 0;
        },
    },


    computed : {
        note(){
            return this.$store.getters.note_by_slug(this.$route.params.note_route);
        },

        current_workspace() {
            return this.$store.getters.current_workspace;
        },

        current_note() {
            return this.$store.getters.current_note;
        },

        note_view_modes() {
            let modes = [this.$store.state.ui.note_view_mode];
            if (this.$store.state.ui.note_options_visible) modes.push('options');
            return modes;
        },

        note_title: {
            get: function() {
                return (this.current_note || {title: ''}).title;
            },

            set: _.debounce(function(val) {
                this.$store.dispatch('updateNote', {...this.note, title: val})
                .then(updatedNote => {
                    this.$router.push(`/${this.$route.workspace_route}/notes/${updatedNote.slug}/edit`)
                });
            }, 300)
            
        },

        isMobile() {
            let check = false;
            (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
            return check;
        },

        note_aliases: {
            get: function() {
                let value = this.current_note && this.current_note.aliases ? this.current_note.aliases : [];
                return value
            },

            set: _.debounce(function(val) {
                this.$store.dispatch('updateNote', {...this.note, aliases: val})
                .then(updatedNote => {
                    this.$router.push(`/${this.$route.workspace_route}/notes/${updatedNote.slug}/edit`)
                })
                .catch(error=> {
                    this.note_alias_error = error.getMessage?error.getMessage():error;
                });
            }, 300)
            
        },

        note_text_with_anchors_fixed() {
            if (!this.current_note) return '';

            let note_text = this.current_note.text;
            return note_text.replace(/<a (.*)\/>/g, (match, p1, offset, string) => {
                return `<a ${p1}></a>`;
            });
        },

        // note_text_with_breaks() {
        //     if (!this.note_text_with_anchors_fixed) return '';
            
        //     let note_text = this.note_text_with_anchors_fixed;
        //     // return note_text.replaceAll("\n\n", "\n<br></br>");
        //     return note_text.replace(/(?<!\()\b[\r|\n]{2}\b(?![\w\s]*[\)])/mg, "BREAKHERE")
        //     return note_text;
        // },

        note_text_with_links() {
                if (!this.note_text_with_anchors_fixed) return '';
                
                let note_text = this.note_text_with_anchors_fixed.replace(/ /g, '  ');
                
                this.links.forEach(link => {
                    let url = `/${this.current_workspace.slug}/notes/${link.slug}`

                    let alias = link.alias_index !== undefined ? link.aliases[link.alias_index] : null;
                    let link_text = alias ? alias : link.title;
                    
                    let full_string = `[${link_text}](${url})`

                    let escaped = link_text.replace(/ /g,  '  ');

                    let regex = new RegExp(`(^|\\s|,|\\!)(${escaped})($|\\s|\\n|,|\\.|\\!|\\))` , 'gmi');
        
                    note_text = note_text.replace(regex, (match, p1, p2, p3, offset, string) => {
                        return p1+`[${p2}](${url})`+p3;
                    });
                })

                note_text = note_text.replace(/\\([a-z|A-Z])/g, (match, p1, offset, string) => {
                    return p1;
                });  

                return note_text.replace(/ {2}/g,' ');
        },

        links() {
            return this.$store.getters.links_for_note;
        },

        note_view_mode() {
            return this.$store.state.ui.note_view_mode;
        },

        editing() {
            return this.note_view_mode === 'edit';
        },
        
        options_modal_open() {
            return this.$store.getters.note_options_visible;
        }
    },

    beforeRouteEnter (to, from, next) {
        next(vm => {
            vm.$store.dispatch("selectNote", to.params.note_route);
        });
    },

    beforeRouteUpdate (to, from, next) {
        this.$store.dispatch("selectNote", to.params.note_route);
        next();
    },
}
</script>

<style lang="sass" scoped>
    @import "../assets/colors.sass";
    .note-view
        padding: 10px
        padding: 0px
        display: flex
        flex-direction: column
        justify-content: flex-start
        height: 100%;
        min-height: 0;
        overflow: hidden;
        position: relative;


    .content
        display: flex
        flex-direction: row
        overflow: hidden
        flex: 1


    .content-segment
        flex: 1
        height: 100%;
        overflow: hidden;
        display: flex;
        @media only screen and (max-width: 720px) 
            &.editor-focused
                flex: 0;


    @media screen and (orientation: portrait )
        .content
            flex-direction: column !important

        #markdown-viewer, #note-editor
            margin-right: 0px;
    
    #editor-wrapper
        order: 1
        @media only screen and (max-width: 720px) 
            order: 2
    
    #markdown-wrapper
        order: 2
        @media only screen and (max-width: 720px) 
            order: 1


    #header 
        display: flex
        flex-direction: column
        // margin: 0 -10px
        padding: 0 20px
        border-bottom: 1px solid $border-medium
        // align-items

    .title-row 
        flex-basis: 60px;

    .header-row
        width: 100%
        flex-grow: 0;
        flex-shrink: 0;

        display: flex
        flex-direction: row
        align-items: center
        justify-content: space-between
        flex-wrap: nowrap;
        background-color: inherit;
        span
            flex: 1;
            white-space: nowrap;
            min-width: 100px;
            display: block;
            overflow: hidden;
        input
            min-width: 100px;
            flex: 1
            flex-shrink: 1;

        #actions
            display: flex
            flex-direction: row
            justify-content: space-around
            // width: 150px
            background-color: $dark-bg
    

   
        
    .note-title, .note-title-input
        font-size: 2em
        height: 45px
        font-family: 'Roboto Slab'
        font-weight: 300
        box-sizing: border-box
        padding: 0; 
        text-align: left;
        // margin-right: 5px;
        text-align: center

        // We seem to get 1px vertical wobble room when switching between 
        //  The input and the presentation. Border fixes it.
        border: 1px solid transparent;

    .note-title-input
        border: 1px dashed $border-medium;
        box-shadow: $mini-shadow;
        padding: 5px;
        // margin-left: -5px;
        
        &:focus
            border-color: $border-light;
        &:active, &:active:focus
            border-color: lighten($border-light, 5%);
    

   

    .markdown-header 
        flex-direction: row
        display: flex
        justify-content: space-between
        align-items: flex-end
        flex-basis: 25px
        flex-grow: 0
        flex-shrink: 0
        flex: 1
        z-index: 1
        height: 24px
        min-width: 50%

        &.half 
            padding-left: 20px
            box-sizing: border-box

        & > .header-button 
            text-transform: uppercase
            font-size: 14px
            line-height: 14px
            vertical-align: middle
            padding: 5px 5px
            height: 24px
            display: flex
            justify-content: center
            align-content: center
            align-items: center
            box-sizing: border-box
            border: 1px solid $border-medium
            background-color: #262626
            position: relative
            
            margin-top: -3px

            &:hover 
                background-color: darken(#262626, 10%)
    
    .edit-toolbar 
        flex: 1
        top: 10px;
        padding-right: 20px

        /deep/ .toolbar-item 
            cursor: pointer
            background-color: $darker-bg
            margin-right: 5px
            border: 1px solid $border-medium
            height: 24px
            font-size: 14px
            line-height: 14px
            padding: 5px
            &:hover 
                background-color: darken(#262626, 10%)
            
    
    .toolbar 
        position: relative
        top: 12px
        margin-top: -10px
        flex-wrap: wrap

        



</style>